import { useEffect, useRef } from 'react'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'

export interface AudioSaveCompleteEvent {
  op: 'audio_save_complete'
  version: number
  success: true
}

export interface AudioSaveFailedEvent {
  op: 'audio_save_failed'
  error: string
  success: false
}

type VideoTransformEvent = AudioSaveCompleteEvent | AudioSaveFailedEvent

interface UseVideoTransformChannelOptions {
  almanacFileKey: string
  onAudioSaveComplete: () => void
  onAudioSaveFailed: (error: string) => void
  enabled?: boolean
}

export const useVideoTransformChannel = ({
  almanacFileKey,
  onAudioSaveComplete,
  onAudioSaveFailed,
  enabled = true,
}: UseVideoTransformChannelOptions) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()

  // Use refs to avoid recreating subscriptions when callbacks change
  const onCompleteRef = useRef(onAudioSaveComplete)
  const onFailedRef = useRef(onAudioSaveFailed)

  useEffect(() => {
    onCompleteRef.current = onAudioSaveComplete
    onFailedRef.current = onAudioSaveFailed
  }, [onAudioSaveComplete, onAudioSaveFailed])

  useEffect(() => {
    if (!auth || !websocketConnection || !enabled || !almanacFileKey) return

    const subscription = websocketConnection.subscriptions.create(
      {
        channel: 'VideoTransformChannel',
        key: almanacFileKey,
      },
      {
        received(data: VideoTransformEvent) {
          if (data.op === 'audio_save_complete') {
            onCompleteRef.current()
          } else if (data.op === 'audio_save_failed') {
            onFailedRef.current(data.error)
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [auth, websocketConnection, almanacFileKey, enabled])
}
